import React, {
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import PropTypes from 'prop-types';
import _ from 'lodash';

import { Provider } from './QueueContext';
import StateContext from '../../../HO/State/StateContext';

const isQueueFinished = (queue) => !!(queue && queue.finished);

const successCalledIds = {};

const callSuccessOnce = (queue, result) => {
  const fn = queue.successFunc;
  if (typeof fn !== 'function' || !queue.id || successCalledIds[queue.id]) {
    return;
  }
  if (result && (result.error || (result.errors || []).length)) {
    return;
  }
  successCalledIds[queue.id] = true;
  fn();
};

const mergeRemoteQueues = (local, remote) => {
  if (!Array.isArray(remote)) {
    return local;
  }

  const localById = _.keyBy(local, 'id');
  const remoteIds = {};
  const merged = [];

  remote.forEach((remoteQueue) => {
    if (!remoteQueue || remoteQueue.id == null) {
      return;
    }

    remoteIds[remoteQueue.id] = true;
    const localQueue = localById[remoteQueue.id];

    if (!localQueue) {
      merged.push(remoteQueue);
      return;
    }

    if (isQueueFinished(localQueue) && !isQueueFinished(remoteQueue)) {
      merged.push(localQueue);
      return;
    }

    // Завершилась на другой вкладке — колбэк живёт только здесь
    if (!isQueueFinished(localQueue) && isQueueFinished(remoteQueue)) {
      callSuccessOnce(localQueue, remoteQueue.result || localQueue.result || {});
    }

    merged.push({
      ...localQueue,
      ...remoteQueue,
      finished: localQueue.finished || remoteQueue.finished,
      result: (isQueueFinished(localQueue) && localQueue.result)
        ? localQueue.result
        : (remoteQueue.result || localQueue.result),
      successFunc: successCalledIds[localQueue.id] ? undefined : localQueue.successFunc,
    });
  });

  local.forEach((localQueue) => {
    if (remoteIds[localQueue.id]) {
      return;
    }
    if (!isQueueFinished(localQueue)) {
      merged.push(localQueue);
    }
  });

  return merged;
};

const QueueProvider = ({ children }) => {
  const { sendRequest } = useContext(StateContext);
  const [queues, setQueues] = useState([]);
  const isInitialMount = useRef(true);

  // Храним актуальный стейт в ref,
  // чтобы setTimeout всегда видел свежие данные без перезапуска эффекта
  const queuesRef = useRef(queues);
  useEffect(() => {
    queuesRef.current = queues;
  }, [queues]);

  // Ссылка на таймер для возможности его очистки
  const timerRef = useRef(null);
  const isPollingActive = useRef(false);

  const addQueue = (id, params) => {
    setQueues((value) => {
      if (_.some(value, (queue) => queue.id === id)) {
        return value;
      }
      return [{
        id,
        percent: 0,
        started: new Date(),
        ...(params || {}),
      }, ...value];
    });
  };

  const dropQueue = (id) => setQueues((value) => _.filter(value, (queue) => (queue.id !== id)));
  const getQueue = (id) => _.find(queues, (queue) => queue.id === id);

  useEffect(() => {
    const handleStorageChange = (e) => {
      // Если изменения произошли в ключе _queues и это сделала ДРУГАЯ вкладка
      if (e.key === '_queues' && e.newValue) {
        try {
          const remoteQueues = JSON.parse(e.newValue);
          setQueues((localQueues) => mergeRemoteQueues(localQueues, remoteQueues));
        } catch (err) {
          console.error('Ошибка синхронизации вкладок:', err);
        }
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);

  // 1. Синхронизация с LocalStorage (отдельно от логики запросов)
  useEffect(() => {
    if (isInitialMount.current) {
      try {
        const qs = JSON.parse(window.localStorage.getItem('_queues') || '[]') || [];
        if (qs.length) setQueues(qs);
      } catch (e) {
        console.error(e);
      }
      isInitialMount.current = false;
    } else {
      window.localStorage.setItem('_queues', JSON.stringify(queues));
    }
  }, [queues]);

  // 2. Основная функция опроса сервера
  const pollJobs = () => {
    clearTimeout(timerRef.current);

    // Берем самый свежий массив из ref
    const currentQueues = queuesRef.current;
    const activeJobs = _.uniq(_.map(_.filter(currentQueues, (q) => !q.finished), (q) => q.id));

    // Если активных задач нет — останавливаем опрос
    if (!activeJobs.length) {
      isPollingActive.current = false;
      return;
    }

    isPollingActive.current = true;

    sendRequest('jobsResult', {
      body: { jobs: activeJobs },
    }).then((response) => {
      if (response && response.success && response.data) {
        let hasActive = false;

        setQueues((latestQueues) => _.map(latestQueues, (queue) => {
          if (queue.finished) return queue;

          const failQueue = (jobResult) => {
            const errors = (
              jobResult && Array.isArray(jobResult.errors) && jobResult.errors.length
            ) ? jobResult.errors : ['ERROR_UNKNOWN'];
            return {
              ...queue,
              percent: 100,
              finished: new Date(),
              result: { ...(jobResult && typeof jobResult === 'object' ? jobResult : {}), error: true, errors },
            };
          };

          // Джоба появилась уже после этого запроса — не считаем ошибкой
          if (!(queue.id in response.data)) {
            hasActive = true; // Новая локальная джоба, которая не участвовала в fetch
            return queue;
          }

          const {
            status_id: statusId,
            percent,
            status_text: text,
            result,
          } = response.data[queue.id] || {};
          const status = Number(statusId);

          if (status === 4) { // Complete
            const safeResult = (result && typeof result === 'object') ? result : {};
            callSuccessOnce(queue, safeResult);
            return {
              ...queue,
              percent: 100,
              finished: new Date(),
              result: safeResult,
              successFunc: undefined,
            };
          }

          // 1 WAITING / 2 RUNNING — оба могут уже писать percent и status_text
          if (status === 1 || status === 2) {
            hasActive = true;
            const next = {
              ...queue,
              percent: Number(percent) || 0,
            };
            if (text) {
              next.text = text;
            }
            return next;
          }

          return failQueue(result); // 3, 0 or unknown
        }));

        // Планируем следующий шаг на основе синхронно вычисленного флага
        if (hasActive) {
          timerRef.current = setTimeout(pollJobs, 5000);
        } else {
          isPollingActive.current = false;
        }
      } else {
        const routeError = response && response.errors && response.errors.route;
        const status = response && response.errors && response.errors.status;
        const isNetworkError = routeError instanceof Error
          || (routeError && routeError.name === 'AbortError');
        const shouldRetrySlowly = isNetworkError || [500, 502, 503, 504].indexOf(status) !== -1;
        timerRef.current = setTimeout(pollJobs, shouldRetrySlowly ? 10000 : 5000);
      }
    }).catch((err) => {
      console.error('Опрос завершился ошибкой:', err);
      // При ошибке сети/сервера Cloud Run пробуем снова через 10 секунд
      // (чтобы не спамить упавший сервер)
      timerRef.current = setTimeout(pollJobs, 10000);
    });
  };

  // 3. Эффект, который следит за появлением НОВЫХ активных задач и запускает опрос
  useEffect(() => {
    const hasUnfinished = _.some(queues, (q) => !q.finished);

    if (hasUnfinished && !isPollingActive.current) {
      pollJobs();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queues]); // Сработает, когда через addQueue добавится новая задача

  // Анмаунт компонента
  useEffect(() => () => clearTimeout(timerRef.current), []);

  return (
    <Provider
      value={{
        queues,
        addQueue,
        dropQueue,
        getQueue,
      }}
    >
      {children}
    </Provider>
  );
};

QueueProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

QueueProvider.defaultProps = {

};

export default QueueProvider;
